import { useMemo } from "react"

import { Input } from "@/components/ui/input"

/**
 * Reusable click-to-toggle multi-select, replacing the hijacked
 * `<select multiple>` from `ui-helpers.js:makeMultiSelectIntuitive`.
 * Supports:
 *
 *   - Search-filter (type to filter visible options; selected items
 *     always remain counted but can be hidden by the filter).
 *   - Per-row checkbox-style toggle on click.
 *   - Optional selection-summary line above the list (parity with
 *     "No countries selected" / "Selected: …").
 */

export interface MultiSelectOption {
  id: string
  name: string
}

interface Props {
  label?: string
  options: MultiSelectOption[]
  selected: string[]
  onToggle: (id: string) => void
  search?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  emptySelectionText?: string
  maxHeightClass?: string
  /** When false, hide the Selected chips block (parent can show a shared summary). */
  showSelectionSummary?: boolean
  /**
   * When false, show all ``options`` regardless of ``search``. Use when the
   * parent already queried an API with that search string (e.g. purchase
   * intention recommendations) so client-side substring filtering does not
   * hide valid results like "Makeup" for query "beauty".
   */
  filterOptionsBySearch?: boolean
}

export function IntuitiveMultiSelect({
  options,
  selected,
  onToggle,
  search,
  onSearchChange,
  searchPlaceholder,
  emptySelectionText,
  maxHeightClass = "max-h-56",
  showSelectionSummary = true,
  filterOptionsBySearch = true,
}: Props) {
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.name.localeCompare(b.name)),
    [options],
  )

  const filtered = useMemo(() => {
    if (!filterOptionsBySearch) return sortedOptions
    const q = (search ?? "").trim().toLowerCase()
    if (!q) return sortedOptions
    return sortedOptions.filter((o) => o.name.toLowerCase().includes(q))
  }, [sortedOptions, search, filterOptionsBySearch])

  const selectedOptions = sortedOptions.filter((o) => selected.includes(o.id))

  return (
    <div>
      {onSearchChange ? (
        <Input
          type="text"
          className="mb-2"
          placeholder={searchPlaceholder ?? "Search..."}
          value={search ?? ""}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      ) : null}
      {showSelectionSummary ? (
        <div className="mb-2 text-sm text-white">
          {selectedOptions.length > 0 ? (
            <>
              <span className="font-semibold text-white">Selected:</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => onToggle(opt.id)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#8fa0ef]/45 bg-[#3a4060] px-2 py-0.5 text-xs text-white hover:bg-[#4a5380]"
                    aria-label={`Remove ${opt.name}`}
                    title={`Remove ${opt.name}`}
                  >
                    <span>{opt.name}</span>
                    <span className="text-[11px] leading-none">×</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            emptySelectionText ?? "None selected"
          )}
        </div>
      ) : null}
      <div
        className={`${maxHeightClass} overflow-y-auto rounded-md border border-[#626262] bg-brand-bg/40`}
      >
        {filtered.length === 0 ? (
          <div className="p-3 text-sm text-white">No options</div>
        ) : (
          filtered.map((opt) => {
            const isSelected = selected.includes(opt.id)
            return (
              <button
                type="button"
                key={opt.id}
                onClick={() => onToggle(opt.id)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[#3f4566] ${
                  isSelected ? "bg-[#454d78] text-white" : ""
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`inline-block h-4 w-4 rounded border border-[#6a6a6a] text-center text-xs leading-4 ${
                    isSelected ? "bg-[#8fa0ef] text-white" : ""
                  }`}
                >
                  {isSelected ? "✓" : ""}
                </span>
                <span>{opt.name}</span>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
